// src/components/AdvancedOrdersPage.js
import React, {useEffect, useState} from "react";
import "./css/AdvancedOrdersPage.css";
import {apiFetch as fetch} from "apiClient";
import AdvancedOrderPopup from "@dashboard/buysellpopup/advancedOrderPopup/AdvancedOrderPopup";
import {submitAdvancedOrder} from "@dashboard/buysellpopup/advancedOrderPopup/submitAdvancedOrder";
import { useLocation } from "react-router-dom";

function formatDate(value) {
    if (!value) return "—";
    const d = new Date(value);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleString();
}

function formatNumber(n, decimals = 2) {
    if (n === null || n === undefined) return "—";
    return Number(n).toFixed(decimals);
}

function statusClass(status) {
    if (!status) return "status-pill status-unknown";
    const s = status.toUpperCase();
    if (s === "RUNNING" || s === "PENDING") return "status-pill status-running";
    if (s === "COMPLETED") return "status-pill status-completed";
    if (s === "CANCELLED") return "status-pill status-cancelled";
    if (s === "ERROR") return "status-pill status-error";
    return "status-pill status-unknown";
}

async function fetchQuote(token) {
    try {
        const res = await fetch(`/api/kite-quotes/live/${token}`);
        if (!res.ok) return null;
        return await res.json();
    } catch (e) {
        console.error("Quote fetch error", e);
        return null;
    }
}

function getPreviousWorkingDay(date) {
    const d = new Date(date);
    d.setDate(d.getDate() - 1);
    let day = d.getDay();
    if (day === 0) d.setDate(d.getDate() - 2); // sunday → friday
    if (day === 6) d.setDate(d.getDate() - 1); // saturday → friday
    return d;
}

const AdvancedOrdersPage = ({ highlightOrderId }) => {
    const [orders, setOrders] = useState([]);
    const [selectedOrderId, setSelectedOrderId] = useState(null);
    const [executions, setExecutions] = useState([]);
    const [loadingOrders, setLoadingOrders] = useState(false);
    const [loadingExecs, setLoadingExecs] = useState(false);
    const [reexecutingId, setReexecutingId] = useState(null);
    const [error, setError] = useState(null);
    const [editingOrder, setEditingOrder] = useState(null);
    const [showPopup, setShowPopup] = useState(false);
    const [deletingId, setDeletingId] = useState(null);
    const [popupSubmitting, setPopupSubmitting] = useState(false);
    const [popupBackendError, setPopupBackendError] = useState("");

    const [lastClosePrice, setLastClosePrice] = useState("");       // keep as "" (not null)
    const [lastClosePriceDate, setLastClosePriceDate] = useState(""); // keep as "" (not null)
// Filters
    const [statusFilter, setStatusFilter] = useState("NON_FINAL"); // default
    const [symbolQuery, setSymbolQuery] = useState("");
    const [createdFrom, setCreatedFrom] = useState(""); // yyyy-mm-dd
    const [createdTo, setCreatedTo] = useState("");     // yyyy-mm-dd
    const [createdSortDir, setCreatedSortDir] = useState("DESC"); // newest first
    const [highlightedId, setHighlightedId] = useState(null);

    const fetchOrders = async () => {
        try {
            setLoadingOrders(true);
            setError(null);
            const res = await fetch("/api/advanced-orders");
            if (!res.ok) {
                throw new Error("Failed to load orders");
            }
            const data = await res.json();
            setOrders(data || []);
            // Keep selection valid
            if (data && data.length > 0) {
                const exists = data.some(o => o.id === selectedOrderId);
                if (!exists) {
                    setSelectedOrderId(data[0].id);
                }
            } else {
                setSelectedOrderId(null);
                setExecutions([]);
            }
        } catch (e) {
            console.error(e);
            setError(e.message || "Error while loading orders");
        } finally {
            setLoadingOrders(false);
        }
    };

    const fetchExecutions = async (orderId) => {
        if (!orderId) return;
        try {
            setLoadingExecs(true);
            setError(null);
            const res = await fetch(`/api/advanced-orders/${orderId}/executions`);
            if (!res.ok) {
                throw new Error("Failed to load child executions");
            }
            const data = await res.json();
            setExecutions(data || []);
        } catch (e) {
            console.error(e);
            setError(e.message || "Error while loading child executions");
        } finally {
            setLoadingExecs(false);
        }
    };

    useEffect(() => {
        fetchOrders();
    }, []);

    useEffect(() => {
        if (selectedOrderId != null) {
            fetchExecutions(selectedOrderId);
        }
    }, [selectedOrderId]);

    const handleSelectOrder = (orderId) => {
        setSelectedOrderId(orderId);
    };

    const handleEdit = async (order) => {
        if (!order?.id) return;

        try {
            setError(null);

            // reset popup UX state
            setPopupBackendError("");
            setPopupSubmitting(false);
            setLastClosePrice("");
            setLastClosePriceDate("");

            // ✅ Always refetch FULL order by id (so nested order.preopen etc is present)
            const res = await fetch(`/api/advanced-orders/${order.id}`);
            if (!res.ok) throw new Error("Failed to load order for editing");
            const fullOrder = await res.json();

            // ✅ If PREOPEN, fetch quote and compute "last close price" + "close date"
            const mode = (fullOrder?.mode || "").toUpperCase();
            if (mode === "PREOPEN") {
                const token =
                    fullOrder.instrumentToken || order.instrumentToken;

                if (token) {
                    const quote = await fetchQuote(token);

                    const close =
                        quote?.ohlc?.close ?? quote?.lastPrice ?? null;

                    // store as "" when missing (so we never pass null)
                    setLastClosePrice(close != null ? String(close) : "");

                    const dateObj = quote?.timestamp
                        ? getPreviousWorkingDay(new Date(quote.timestamp))
                        : null;

                    setLastClosePriceDate(
                        dateObj ? dateObj.toISOString().slice(0, 10) : ""
                    );
                }
            }

            setEditingOrder(fullOrder);
            setShowPopup(true);
        } catch (e) {
            console.error(e);
            setError(e.message || "Error while opening order for edit");
        }
    };

    useEffect(() => {
        if (!highlightOrderId) return;

        setSelectedOrderId(highlightOrderId);
        setHighlightedId(highlightOrderId);

        setTimeout(() => {
            document
                .getElementById(`ao-card-${highlightOrderId}`)
                ?.scrollIntoView({ block: "center", behavior: "smooth" });
        }, 50);

        const t = setTimeout(() => setHighlightedId(null), 6000);
        return () => clearTimeout(t);
    }, [highlightOrderId]);

    const handlePopupConfirm = async (payload) => {
        if (!editingOrder) return;

        setPopupBackendError("");
        setPopupSubmitting(true);

        try {
            const result = await submitAdvancedOrder(payload, {
                instrument: editingOrder,          // must have tradingsymbol
                segment: editingOrder.exchange,    // required by submitAdvancedOrder :contentReference[oaicite:8]{index=8}
                depthInstrumentToken: editingOrder.instrumentToken,
                existingOrder: editingOrder,
            });

            if (!result.success) {
                setPopupBackendError(result.message || "Failed");
                return;
            }

            setShowPopup(false);
            setEditingOrder(null);
            await fetchOrders();
        } finally {
            setPopupSubmitting(false);
        }
    };

    const handleDelete = async (order) => {
        if (!order?.id) return;

        try {
            setError(null);

            // 1) backend precheck (optional endpoint)
            const pre = await fetch(`/api/advanced-orders/${order.id}/cancel-precheck`);
            const preJson = pre.ok ? await pre.json() : null;

            if (preJson?.success && preJson?.hasLiveExposureChildren) {
                const ok = window.confirm(
                    preJson.message || "Order has live/unreconciled child executions. Cancel anyway?"
                );
                if (!ok) return;
            } else {
                const ok = window.confirm(`Delete order ${order.id}? It will attempt to cancel first.`);
                if (!ok) return;
            }

            setDeletingId(order.id);

            // 2) cancel first
            const cancelRes = await fetch(`/api/advanced-orders/${order.id}/cancel`, { method: "POST" });
            const cancelJson = await cancelRes.json().catch(() => ({}));

            if (!cancelRes.ok || !cancelJson.success) {
                alert(`⚠️ Cancel failed: ${cancelJson.message || "Unknown error"}`);
                return;
            }

            // 3) re-fetch order to ensure it reached a final state
            const refetched = await fetch(`/api/advanced-orders/${order.id}`);
            const updatedOrder = await refetched.json().catch(() => ({}));

            const status = (updatedOrder.status || "").toUpperCase();
            if (!["CANCELLED", "COMPLETED", "ERROR"].includes(status)) {
                alert(
                    `⚠️ Order ${order.id} is not in a final state yet. Current status: ${status}. ` +
                    `Try again after a few seconds.`
                );
                return;
            }

            // 4) delete
            const deleteRes = await fetch(`/api/advanced-orders/${order.id}`, { method: "DELETE" });
            const deleteJson = await deleteRes.json().catch(() => ({}));

            if (!deleteRes.ok || !deleteJson.success) {
                alert(`❌ Failed to delete: ${deleteJson.message || "Server rejected delete."}`);
                return;
            }

            // 5) UI cleanup
            setEditingOrder(null);
            setShowPopup(false);
            setSelectedOrderId(null);
            setExecutions([]);
            await fetchOrders();

            alert(`✔️ Order ${order.id} deleted successfully.`);
        } catch (err) {
            console.error(err);
            alert(`❌ Unexpected error deleting order: ${err.message || err}`);
        } finally {
            setDeletingId(null);
        }
    };


    const handleReexecute = async (order) => {
        if (!order) return;
        const confirmed = window.confirm(
            `Re-execute advanced order #${order.id} (${order.side} ${order.tradingsymbol})?`
        );
        if (!confirmed) return;

        try {
            setReexecutingId(order.id);
            setError(null);
            const res = await fetch(`/api/advanced-orders/${order.id}/reexecute`, {
                method: "POST",
                headers: {"Content-Type": "application/json"},
                body: JSON.stringify({})
            });
            if (!res.ok) {
                const txt = await res.text();
                throw new Error(txt || "Failed to re-execute order");
            }

            // Optionally you can read JSON response here
            await fetchOrders();
            if (selectedOrderId === order.id) {
                await fetchExecutions(order.id);
            }
        } catch (e) {
            console.error(e);
            setError(e.message || "Error while re-executing order");
        } finally {
            setReexecutingId(null);
        }
    };

    const selectedOrder = orders.find(o => o.id === selectedOrderId) || null;

    const FINAL_STATUSES = new Set(["COMPLETED", "CANCELLED", "ERROR"]);

    function normalizeStatus(s) {
        return (s || "").toUpperCase().trim();
    }

    function getHighlightId(location) {
        const sp = new URLSearchParams(location.search);
        const raw = sp.get("highlightOrderId");
        const n = raw ? Number(raw) : null;
        return Number.isFinite(n) ? n : null;
    }


    function matchesStatusFilter(order, filter) {
        const st = normalizeStatus(order?.status);

        switch (filter) {
            case "ALL":
                return true;

            case "FINAL":
                return FINAL_STATUSES.has(st);

            case "NON_FINAL":
                return !FINAL_STATUSES.has(st);

            case "CANCELLED":
                return st === "CANCELLED";

            case "ERROR":
                return st === "ERROR";

            case "PENDING":
                return st === "PENDING" || st === "RUNNING";

            case "COMPLETED":
                return st === "COMPLETED";

            default:
                return true;
        }
    }


    function parseDateOnly(yyyyMmDd) {
        if (!yyyyMmDd) return null;
        const d = new Date(yyyyMmDd + "T00:00:00");
        return isNaN(d.getTime()) ? null : d;
    }

    function matchesCreatedRange(order, fromStr, toStr) {
        if (!fromStr && !toStr) return true;

        const created = order?.createdAt ? new Date(order.createdAt) : null;
        if (!created || isNaN(created.getTime())) return false;

        const from = parseDateOnly(fromStr);
        const to = parseDateOnly(toStr);

        if (from && created < from) return false;

        if (to) {
            const toEnd = new Date(to);
            toEnd.setHours(23, 59, 59, 999);
            if (created > toEnd) return false;
        }

        return true;
    }

    function matchesSymbol(order, q) {
        const query = (q || "").trim().toUpperCase();
        if (!query) return true;
        const sym = (order?.tradingsymbol || "").toUpperCase();
        return sym.includes(query);
    }

    const filteredOrders = (orders || [])
        .filter(o =>
            matchesStatusFilter(o, statusFilter) &&
            matchesSymbol(o, symbolQuery) &&
            matchesCreatedRange(o, createdFrom, createdTo)
        )
        .sort((a, b) => {
            const ta = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
            const tb = b?.createdAt ? new Date(b.createdAt).getTime() : 0;
            return createdSortDir === "ASC" ? (ta - tb) : (tb - ta);
        });


    useEffect(() => {
        if (!filteredOrders || filteredOrders.length === 0) {
            setSelectedOrderId(null);
            setExecutions([]);
            return;
        }
        const exists = filteredOrders.some(o => o.id === selectedOrderId);
        if (!exists) {
            setSelectedOrderId(filteredOrders[0].id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusFilter, symbolQuery, createdFrom, createdTo, orders]);

    return (
        <div className="ao-page">
            <div className="ao-header">
                <div>
                    <h1>Advanced Orders</h1>
                    <p className="ao-subtitle">
                        View all algo orders, monitor child executions, and re-execute if needed.
                    </p>
                </div>
                <div className="ao-filters">
                    <div className="ao-filter-row">
                        <div className={"aop-type-filter"}>
                            <label className="ao-filter-label">Show</label>
                            <select
                                className="ao-filter-select"
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                            >
                                <option value="NON_FINAL">Non-final (default)</option>
                                <option value="FINAL">Final</option>
                                <option value="PENDING">Pending</option>
                                <option value="COMPLETED">Completed</option>
                                <option value="CANCELLED">Cancelled</option>
                                <option value="ERROR">Error</option>
                                <option value="ALL">All</option>
                            </select>
                        </div>
                        <div className="ao-search">
                            <input
                                className="ao-filter-search"
                                value={symbolQuery}
                                onChange={(e) => setSymbolQuery(e.target.value)}
                                placeholder="Search symbol…"
                            />
                            <button
                                className="ao-btn ao-btn-secondary ao-btn-sm"
                                onClick={() => {
                                    setStatusFilter("NON_FINAL");
                                    setSymbolQuery("");
                                    setCreatedFrom("");
                                    setCreatedTo("");
                                }}
                            >
                                Reset
                            </button>
                        </div>
                        <div className="aop-created-filter">
                            <label className="ao-filter-label">Created</label>

                            <input
                                type="date"
                                className="ao-filter-date"
                                value={createdFrom}
                                onChange={(e) => setCreatedFrom(e.target.value)}
                            />
                            <span className="ao-filter-to">to</span>
                            <input
                                type="date"
                                className="ao-filter-date"
                                value={createdTo}
                                onChange={(e) => setCreatedTo(e.target.value)}
                            />
                        </div>
                    </div>
                </div>

                {selectedOrder && (
                    <div className="ao-header-actions">
                        <button
                            className="ao-btn ao-btn-secondary ao-btn-sm"
                            disabled={["COMPLETED", "CANCELLED"].includes((selectedOrder.status || "").toUpperCase())}
                            onClick={() => handleEdit(selectedOrder)}
                        >
                            Edit Selected
                        </button>
                    </div>
                )}
            </div>

            {error && (
                <div className="ao-alert ao-alert-error">
                    {error}
                </div>
            )}

            <div className="ao-layout">
                <div className="ao-orders-panel">
                    <div className="ao-panel-header">
                        <h2>Orders</h2>
                        <div className={"aop-sort-div"}>
                            <label className="ao-filter-label">Sort:</label>
                            <button
                                className="ao-btn ao-btn-secondary ao-btn-sm"
                                type="button"
                                onClick={() => setCreatedSortDir(d => (d === "ASC" ? "DESC" : "ASC"))}
                                title="Toggle Created date sort"
                            >
                                Created {createdSortDir === "ASC" ? "↑" : "↓"}
                            </button>
                        </div>
                        <span className="ao-count">{filteredOrders.length} / {orders.length}</span>
                    </div>


                    {orders.length === 0 && !loadingOrders && (
                        <div className="ao-empty">No advanced orders found.</div>
                    )}

                    {loadingOrders && (
                        <div className="ao-loading">Loading orders…</div>
                    )}

                    <div className="ao-orders-list">
                        {filteredOrders.map(order => (
                            <OrderCard
                                key={order.id}
                                order={order}
                                selected={order.id === selectedOrderId}
                                onSelect={() => handleSelectOrder(order.id)}
                                onReexecute={() => handleReexecute(order)}
                                reexecuting={reexecutingId === order.id}
                                onEdit={() => handleEdit(order)}
                                onDelete={() => handleDelete(order)}
                                deletingId={deletingId}
                                highlighted={order.id === highlightedId}

                            />
                        ))}
                    </div>
                </div>

                <div className="ao-details-panel">
                    <div className="ao-panel-header">
                        <h2>Details</h2>
                    </div>

                    {!selectedOrder && (
                        <div className="ao-empty">
                            Select an order from the left to see details and child executions.
                        </div>
                    )}

                    {selectedOrder && (
                        <>
                            <OrderDetails order={selectedOrder}/>
                            <ExecutionsTable
                                executions={executions}
                                loading={loadingExecs}
                            />
                        </>
                    )}
                </div>
            </div>
            {showPopup && (
                <AdvancedOrderPopup
                    open={showPopup}
                    order={editingOrder}
                    side={editingOrder?.side}
                    tradingsymbol={editingOrder?.tradingsymbol}
                    basePrice={editingOrder?.targetPrice ?? editingOrder?.price}
                    onClose={() => {
                        setShowPopup(false);
                        setEditingOrder(null);

                        // cleanup
                        setPopupBackendError("");
                        setPopupSubmitting(false);
                        setLastClosePrice("");
                        setLastClosePriceDate("");
                    }}
                    onConfirm={handlePopupConfirm}

                    // ✅ UX props (same as BuySellPopup)
                    backendError={popupBackendError}
                    submitting={popupSubmitting}

                    // ✅ PREOPEN read-only fields: pass "" (not null)
                    lastClosePrice={lastClosePrice}
                    lastClosePriceDate={lastClosePriceDate}
                />
            )}

        </div>
    );
};

const OrderCard = ({
                       order,
                       selected,
                       highlighted,   // 👈 ADD THIS
                       onSelect,
                       onReexecute,
                       reexecuting,
                       onEdit,
                       onDelete,
                       deletingId
                   }) => {

    const expected = order.expectedQuantity ?? 0;
    const executed = order.executedQuantity ?? 0;
    const progress = expected > 0 ? Math.round((executed / expected) * 100) : 0;

    const isFinal =
        ["COMPLETED", "CANCELLED", "ERROR"].includes(
            (order.status || "").toUpperCase()
        );

    return (
        <div
            id={`ao-card-${order.id}`}   // 👈 REQUIRED
            className={
                `ao-order-card ${selected ? "ao-order-card-selected" : ""} ` +
                (highlighted ? "ao-order-card-highlight" : "")
            }
            onClick={onSelect}
        >

        <div className="ao-order-main-row">
                <div className="ao-order-symbol">
                    <span className="ao-symbol">{order.tradingsymbol}</span>
                    <span className={`ao-side-badge ao-side-${(order.side || "").toLowerCase()}`}>
                        {order.side}
                    </span>
                </div>
                <span className={statusClass(order.status)}>
                    {order.status}
                </span>
            </div>

            <div className="ao-order-sub-row">
                <span className="ao-label-pill">
                    {order.mode} • {order.executionMode || "AGGRESSIVE"}
                </span>
                <span className="ao-label-pill">
                    {order.exchange} • Token {order.instrumentToken}
                </span>
            </div>

            <div className="ao-progress-row">
                <div className="ao-progress-label">
                    Executed {executed} / {expected} ({progress}%)
                </div>
                <div className="ao-progress-bar">
                    <div
                        className="ao-progress-bar-fill"
                        style={{width: `${Math.min(progress, 100)}%`}}
                    />
                </div>
            </div>

            <div className="ao-order-meta-row">
                <div className="ao-meta-col">
                    <span className="ao-meta-label">Created</span>
                    <span className="ao-meta-value">
                        {formatDate(order.createdAt)}
                    </span>
                </div>
                <div className="ao-meta-col">
                    <span className="ao-meta-label">Last exec price</span>
                    <span className="ao-meta-value">
                        {formatNumber(order.lastExecutedPrice)}
                    </span>
                </div>
            </div>

            <div className="ao-order-actions">
                <button
                    className="ao-btn ao-btn-primary ao-btn-sm"
                    onClick={(e) => {
                        e.stopPropagation();
                        onReexecute();
                    }}
                    disabled={reexecuting}
                >
                    {reexecuting ? "Re-executing…" : "Re-execute"}
                </button>
                <button
                    className="ao-btn ao-btn-secondary ao-btn-sm"
                    onClick={(e) => {
                        e.stopPropagation();
                        onEdit(order);
                    }}
                >
                    Edit
                </button>

                <button
                    className="ao-btn ao-btn-danger ao-btn-sm"
                    disabled={deletingId === order.id}
                    onClick={(e) => {
                        e.stopPropagation();
                        onDelete(order);
                    }}
                >
                    {deletingId === order.id ? "Deleting…" : "Delete"}
                </button>

                {order.errorMessage && (
                    <span className="ao-error-tag">Has error</span>
                )}
                {isFinal && (
                    <span className="ao-final-tag">Final</span>
                )}
            </div>
        </div>
    );
};

const OrderDetails = ({order}) => {
    const status = (order.status || "").toUpperCase();
    const isRange = (order.mode || "").toUpperCase() === "RANGE";
    const isConsistent = (order.mode || "").toUpperCase() === "CONSISTENT";

    return (
        <div className="ao-details-card">
            <div className="ao-details-header">
                <div>
                    <h3>
                        #{order.id} • {order.side} {order.tradingsymbol}
                    </h3>
                    <p className="ao-details-subtitle">
                        {order.mode} mode • {order.executionMode || "AGGRESSIVE"} • {order.exchange}
                    </p>
                </div>
                <span className={statusClass(order.status)}>
                    {status}
                </span>
            </div>

            <div className="ao-grid">
                <div className="ao-grid-item">
                    <span className="ao-meta-label">Expected Qty</span>
                    <span className="ao-meta-value">
                        {order.expectedQuantity}
                    </span>
                </div>
                <div className="ao-grid-item">
                    <span className="ao-meta-label">Executed Qty</span>
                    <span className="ao-meta-value">
                        {order.executedQuantity}
                    </span>
                </div>
                <div className="ao-grid-item">
                    <span className="ao-meta-label">Remaining Qty</span>
                    <span className="ao-meta-value">
                        {Math.max(
                            0,
                            (order.expectedQuantity || 0) -
                            (order.executedQuantity || 0)
                        )}
                    </span>
                </div>
                {isConsistent && (
                    <div className="ao-grid-item">
                        <span className="ao-meta-label">Target Price</span>
                        <span className="ao-meta-value">
                            {formatNumber(order.targetPrice)}
                        </span>
                    </div>
                )}
                {isRange && (
                    <>
                        <div className="ao-grid-item">
                            <span className="ao-meta-label">Band</span>
                            <span className="ao-meta-value">
                                {formatNumber(order.minPrice)} –{" "}
                                {formatNumber(order.maxPrice)}
                            </span>
                        </div>
                        <div className="ao-grid-item">
                            <span className="ao-meta-label">Accepted Price</span>
                            <span className="ao-meta-value">
                                {formatNumber(order.currentAcceptedPrice)}
                            </span>
                        </div>
                        <div className="ao-grid-item">
                            <span className="ao-meta-label">Delay (min)</span>
                            <span className="ao-meta-value">
                                {order.delayMinutes ?? "—"}
                            </span>
                        </div>
                    </>
                )}
                <div className="ao-grid-item">
                    <span className="ao-meta-label">Created At</span>
                    <span className="ao-meta-value">
                        {formatDate(order.createdAt)}
                    </span>
                </div>
                <div className="ao-grid-item">
                    <span className="ao-meta-label">Last Order Placed</span>
                    <span className="ao-meta-value">
                        {formatDate(order.lastOrderPlacedAt)}
                    </span>
                </div>
                <div className="ao-grid-item">
                    <span className="ao-meta-label">Pause Until</span>
                    <span className="ao-meta-value">
                        {formatDate(order.pauseUntil)}
                    </span>
                </div>
                {order.executionMode &&
                    order.executionMode.toUpperCase() === "RELAXED" && (
                        <div className="ao-grid-item">
                            <span className="ao-meta-label">Total Duration (hrs)</span>
                            <span className="ao-meta-value">
                                {order.totalDurationHours}
                            </span>
                        </div>
                    )}
                <div className="ao-grid-item">
                    <span className="ao-meta-label">Cancel Window Count</span>
                    <span className="ao-meta-value">
                        {order.cancelCountInWindow ?? 0}
                    </span>
                </div>
                <div className="ao-grid-item">
                    <span className="ao-meta-label">Last Exec Price</span>
                    <span className="ao-meta-value">
                        {formatNumber(order.lastExecutedPrice)}
                    </span>
                </div>
            </div>

            {order.errorMessage && (
                <div className="ao-error-block">
                    <div className="ao-error-title">Engine Error</div>
                    <div className="ao-error-text">{order.errorMessage}</div>
                </div>
            )}
        </div>
    );
};

const ExecutionsTable = ({executions, loading}) => {
    return (
        <div className="ao-execs-card">
            <div className="ao-panel-header">
                <h3>Child Executions</h3>
                <span className="ao-count">{executions.length} rows</span>
            </div>

            {loading && (
                <div className="ao-loading">Loading executions…</div>
            )}

            {!loading && executions.length === 0 && (
                <div className="ao-empty">
                    No child executions yet for this order.
                </div>
            )}

            {!loading && executions.length > 0 && (
                <div className="ao-table-wrapper">
                    <table className="ao-table">
                        <thead>
                        <tr>
                            <th>ID</th>
                            <th>Broker Order ID</th>
                            <th>Price</th>
                            <th>Qty Req</th>
                            <th>Qty Filled</th>
                            <th>Status</th>
                            <th>Placed At</th>
                            <th>Completed At</th>
                            <th>Error</th>
                        </tr>
                        </thead>
                        <tbody>
                        {executions.map(exec => (
                            <tr key={exec.id}>
                                <td>{exec.id}</td>
                                <td>{exec.kiteOrderId}</td>
                                <td>{formatNumber(exec.price)}</td>
                                <td>{exec.requestedQuantity}</td>
                                <td>{exec.filledQuantity}</td>
                                <td>{exec.status}</td>
                                <td>{formatDate(exec.placedAt)}</td>
                                <td>{formatDate(exec.completedAt)}</td>
                                <td className="ao-cell-error">
                                    {exec.errorMessage || "—"}
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default AdvancedOrdersPage;
